import { ReportHeader } from "views/applications-id-report/components/ReportHeader";
import { Section } from "views/applications-id-report/components/Section";
import { Stat } from "views/applications-id-report/components/Stat";
import { capitalize, formatPhone, has, toMoney } from "shared/utils";
import moment from "moment";
import {
  getVerificationTitleName,
  incomeTypeMap,
  verificationSourceType
} from "./utils";
import { ContentTitle } from "views/applications-id-report/components/ContentTitle";
import { KeyValue } from "views/applications-id-report/components/KeyValue";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { RoundedMathSymbol } from "shared/app/VerificationTemplate/components/RoundedMathSymbol";
import { CreditCardIcon, Home05Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { CombinedIncomeReport } from "./types";
import { TablesSlice } from "./slices/TablesSlice";
import { Logo } from "./slices/TablesSlice/components/Logo";
import { HousingAssistanceSlice } from "./slices/TablesSlice/HousingAssistanceSlice";
import { NetIncomeChart } from "./slices/NetIncomeGraphSlice";
import { CheckmarkBadge01Icon } from "@hugeicons-pro/core-solid-rounded";
import NoIncomeIcon from "shared/icons/noIncomeIcon.svg?react";
import { ApplicationStatus, VerificationNames } from "shared/types";
import { useQuery } from "@tanstack/react-query";
import { VerificationReport } from "types/landlord/reports";
import { getVerificationQueryKeys } from "views/applications-id-report/utils/getVerificationQueryKeys";
import api from "api/landlord/applications";
import { Loader } from "shared/components/Loader";
import { ReportException } from "views/applications-id-report/components/ReportException";
import { useState } from "react";
import { Applicant } from "types/application";
import { EditIncomeModal } from "./components/EditIncomeModal";
import { formatMonths, mapNetIncomeAnalysis } from "./helpers";
import { AssetItem } from "views/applications-id-report/components/SmartRender/AssetItem";
import { SummaryUpdated } from "./components/SummaryUpdated";
import { ExpandableTable } from "./components/UniversalTable";

const getMonthlyAmount = (amountCents: number, payPeriod: string): number => {
  switch (payPeriod) {
    case "weekly":
      return (amountCents * 52) / 12;
    case "bi-weekly":
      return (amountCents * 26) / 12;
    case "annually":
      return amountCents / 12;
    default:
      return amountCents; // Assuming monthly if not specified
  }
};

export const CombinedIncomeReportSlice = ({
  verificationId,
  applicantId,
  applicationId,
  hasCreditReport,
  applicant,
  applicationStatus
}: {
  verificationId?: number;
  applicantId?: number;
  applicationId?: number;
  hasCreditReport?: boolean;
  applicant: Applicant;
  applicationStatus: ApplicationStatus;
}) => {
  const [showEditIncomeModal, setShowEditIncomeModal] = useState<
    | false
    | {
        grossMonthlyIncomeCents: number;
        netMonthlyIncomeCents: number;
        hasNetMonthlyIncome: boolean;
        incomeId?: number;
        isTotal: boolean;
      }
  >(false);

  const {
    data: reportData,
    isError,
    isLoading,
    refetch
  } = useQuery<VerificationReport<CombinedIncomeReport>>({
    queryKey: getVerificationQueryKeys({
      reportName: VerificationNames.PetScreeningReport,
      applicationId: Number(applicationId || 0),
      applicantId: Number(applicantId || 0),
      verificationId
    }),
    queryFn: () =>
      api
        .getApplicantVerification(
          Number(applicationId || 0),
          Number(applicantId || 0),
          verificationId || 0
        )
        .then((data) => data.verification)
  });
  const data = reportData?.report;

  const noIncome = data?.incomes.find((income) => income.type === "no_income");

  if (noIncome || data?.incomes.length === 0)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <div className="flex flex-col items-center gap-6 p-12">
          <div className="rounded-full bg-alpha-20 p-[30px]">
            <NoIncomeIcon className="text-content-400" />
          </div>
          <div className="space-y-2 text-center">
            <h3 className="font-bold text-base text-content-500">
              Applicant doesn&apos;t have income
            </h3>
            {noIncome?.additional_data.description ? (
              <p className="text-content-300">
                Current financial situation:{" "}
                {noIncome.additional_data.description}
              </p>
            ) : null}
          </div>
        </div>
      </Section>
    );

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <Section maxWidth="none" sectionTitle="Income summary">
        <ReportException
          title="Failed to load income report"
          applicationStatus={applicationStatus}
          applicationId={applicationId || "0"}
        />
      </Section>
    );

  if (!data) return null;

  const summary = data.total_summary,
    debts = summary.debts,
    netIncomeData = data.net_income_analysis,
    incomes = data.incomes;

  return (
    <div>
      <ReportHeader
        applicationStatus={applicationStatus}
        applicationId={applicationId || "0"}
        title="Income history"
        applicantId={0}
        name={VerificationNames.CombinedIncome}
        hidePrint
        reportDate={new Date()}
      />
      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        rightElement={
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            onClick={() =>
              setShowEditIncomeModal({
                grossMonthlyIncomeCents:
                  data.total_overrides?.gross_monthly_income !== undefined
                    ? data.total_overrides.gross_monthly_income * 100
                    : summary.self_reported_gross_monthly_total * 100,
                netMonthlyIncomeCents:
                  data.total_overrides?.net_monthly_income !== undefined
                    ? data.total_overrides.net_monthly_income * 100
                    : summary.verified_net_monthly_total * 100,
                hasNetMonthlyIncome:
                  typeof summary.verified_net_monthly_total === "number",
                isTotal: true
              })
            }
          >
            Edit total income
          </Button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Stat
            title="Verified gross monthly"
            value={toMoney(summary.verified_gross_monthly_total)}
          />
          <Stat
            title="Verified net monthly"
            value={
              data.total_overrides?.net_monthly_income !== undefined
                ? toMoney(data.total_overrides.net_monthly_income)
                : toMoney(summary.verified_net_monthly_total)
            }
          />
          <Show if={hasCreditReport}>
            <Stat
              title="Debt to income ratio"
              value={
                typeof summary.debt_income_ratio === "undefined" ||
                summary.debt_income_ratio === null
                  ? "-"
                  : summary.debt_income_ratio + "%"
              }
            />
          </Show>

          <Stat
            title="Self-reported gross monthly"
            value={
              data.total_overrides?.gross_monthly_income !== undefined
                ? toMoney(data.total_overrides.gross_monthly_income)
                : toMoney(summary.self_reported_gross_monthly_total)
            }
          />
          <Stat
            title="Monthly assistance amount"
            value={toMoney(summary.monthly_housing_assistance)}
          />
          <Stat
            title="Verified income sources"
            value={
              summary.verified_incomes_counter.verified +
              " / " +
              summary.verified_incomes_counter.total
            }
          />
        </div>
        <Show
          if={
            has(data.total_overrides?.gross_monthly_income) ||
            has(data.total_overrides?.net_monthly_income)
          }
        >
          <SummaryUpdated
            changes={data.total_overrides}
            original={{
              gross: summary.self_reported_gross_monthly_total,
              net: summary.verified_net_monthly_total
            }}
          />
        </Show>
      </Section>
      <Section sectionTitle="Net income analysis" maxWidth="1100px">
        <NetIncomeChart data={netIncomeData} />

        <div className="mt-[60px]">
          <ExpandableTable
            hideHeader
            rowClassName="items-center pr-4"
            headerRowClassName="text-content-1000 text-sm font-medium"
            columns={[
              {
                header: "month",
                accessor: (row) => moment(row.month).format("MMMM, YYYY"),
                widthClass: "w-full"
              },
              {
                header: "total",
                accessor: (row) => toMoney(row.total),
                widthClass: "w-[120px] shrink-0"
              }
            ]}
            rows={mapNetIncomeAnalysis(netIncomeData)}
            renderExpanded={(original) => {
              return (
                <div className="bg-background-900 border-t border-alpha-100 px-5">
                  {original.income.map((i, iIdx) => {
                    return (
                      <div
                        key={iIdx}
                        className="border-l !text-sm border-content-100 border-dashed h-[72px] flex items-center w-full relative"
                      >
                        {/* is Last idx */}
                        <Show if={iIdx === original.income.length - 1}>
                          <div className="bg-background-900 w-1 h-9 absolute -left-0.5 translate-y-1/2" />
                        </Show>
                        <div className="flex items-center w-full">
                          <div className="flex items-center shrink-0">
                            <div className="w-6 border-t border-content-100 border-dashed h-px" />
                            <div className="bg-content-100 h-1.5 w-1.5 rounded-full" />
                          </div>

                          <p className="ml-3 text-sm font-medium w-[140px]">
                            {moment(i.month).format("ll")}
                          </p>

                          <div className="flex justify-between items-center ml-4 flex-1">
                            <p className="flex items-center gap-2">
                              <span
                                className="rounded-full block h-2 w-2 shrink-0"
                                style={{
                                  backgroundColor: i.color
                                }}
                              ></span>
                              {i.company}
                            </p>
                            <p>{toMoney(i.income)}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }}
          />
        </div>
      </Section>
      {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
      {incomes.map((income, index) => {
        const diff = moment().diff(moment(income.start_date), "months");

        let annualIncomeCents = 0;
        switch (income.pay_period) {
          case "weekly":
            annualIncomeCents = income.amount_cents * 52;
            break;
          case "bi-weekly":
            annualIncomeCents = income.amount_cents * 26;
            break;
          case "monthly":
            annualIncomeCents = income.amount_cents * 12;
            break;
          case "yearly":
          default:
            annualIncomeCents = income.amount_cents;
        }

        if (income.type === "housing_assistance")
          return (
            <HousingAssistanceSlice
              amountCents={income.amount_cents}
              assets={income.assets}
              key={index}
            />
          );
        return (
          <Section
            maxWidth="1000px"
            key={index}
            sectionTitle={
              <p className="flex items-center gap-2 text-content-400 font-medium text-sm">
                <HugeiconsIcon
                  size={16}
                  icon={CheckmarkBadge01Icon}
                  className="text-primary-500"
                />

                {income.additional_data &&
                "company_name" in income.additional_data
                  ? income.additional_data.company_name
                  : ""}
                <span className="block h-1.5 w-1.5 shrink-0 bg-content-100 rounded-full"></span>
                {incomeTypeMap[income.type] || income.type}
              </p>
            }
            rightElement={
              <p className="text-content-400 text-sm font-medium">
                {moment(income.start_date).format("ll")} - Present (
                {formatMonths(diff)})
              </p>
            }
          >
            <div className="flex items-start">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Stat
                  title="Verified gross monthly"
                  value={toMoney(income.verified_monthly_amounts.gross)}
                />
                <Stat
                  title="Verified net monthly"
                  value={
                    income.additional_data?.overrides?.net_monthly_income !==
                    undefined
                      ? toMoney(
                          income.additional_data.overrides.net_monthly_income
                        )
                      : toMoney(income.verified_monthly_amounts.net)
                  }
                />
                <Stat
                  title="Self-reported gross monthly"
                  value={
                    income.additional_data?.overrides?.gross_monthly_income !==
                    undefined
                      ? toMoney(
                          income.additional_data.overrides.gross_monthly_income
                        )
                      : toMoney(
                          getMonthlyAmount(
                            income.amount_cents,
                            income.pay_period
                          ) / 100
                        )
                  }
                />
              </div>

              <Button
                variant={ButtonVariant.TEXT}
                className="shrink-0"
                size={ButtonSize.SM}
                onClick={() => {
                  let netMonthlyIncomeCents = 0;

                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- array can be empty
                  if (income.items[0])
                    if ("net_monthly_income" in income.items[0].data)
                      netMonthlyIncomeCents =
                        income.items[0].data.net_monthly_income.cents;
                    else if (
                      "net_monthly_income_amount" in income.items[0].data
                    )
                      netMonthlyIncomeCents =
                        income.items[0].data.net_monthly_income_amount * 100;

                  setShowEditIncomeModal({
                    grossMonthlyIncomeCents:
                      income.additional_data?.overrides
                        ?.gross_monthly_income !== undefined
                        ? income.additional_data.overrides
                            .gross_monthly_income * 100
                        : income.amount_cents,
                    netMonthlyIncomeCents:
                      income.additional_data?.overrides?.net_monthly_income !==
                      undefined
                        ? income.additional_data.overrides.net_monthly_income *
                          100
                        : netMonthlyIncomeCents,
                    hasNetMonthlyIncome: income.type !== "offer_letter",
                    incomeId: income.id,
                    isTotal: false
                  });
                }}
              >
                Edit income source
              </Button>
            </div>
            <Show
              if={
                has(income.additional_data?.overrides?.gross_monthly_income) ||
                has(income.additional_data?.overrides?.net_monthly_income)
              }
            >
              <SummaryUpdated
                className="mb-10"
                changes={income.additional_data?.overrides}
                original={{
                  gross: income.amount_cents,
                  net: income.verified_monthly_amounts.net
                }}
              />
            </Show>

            <ContentTitle
              title="Self-reported details"
              contentClassName="space-y-3"
            >
              <Show if={income.type}>
                <KeyValue
                  label="Income type"
                  value={incomeTypeMap[income.type] || income.type}
                />
              </Show>
              {income.additional_data &&
              "company_name" in income.additional_data &&
              income.additional_data.company_name ? (
                <KeyValue
                  label="Company name"
                  value={income.additional_data.company_name}
                />
              ) : null}
              {income.additional_data &&
              "job_title" in income.additional_data &&
              income.additional_data.job_title ? (
                <KeyValue
                  label="Job title"
                  value={income.additional_data.job_title}
                />
              ) : null}
              <Show if={income.start_date}>
                <KeyValue
                  label="Start date"
                  value={moment(income.start_date).format("ll")}
                />
              </Show>
              <Show
                if={
                  income.additional_data &&
                  "supervisor" in income.additional_data &&
                  Boolean(income.additional_data.supervisor?.first_name) &&
                  Boolean(income.additional_data.supervisor?.first_name)
                }
              >
                <KeyValue
                  label="Supervisor name"
                  value={
                    income.additional_data &&
                    "supervisor" in income.additional_data &&
                    income.additional_data.supervisor?.first_name &&
                    income.additional_data.supervisor.last_name
                      ? `${income.additional_data.supervisor.first_name} ${income.additional_data.supervisor.last_name}`
                      : undefined
                  }
                />
              </Show>
              <Show
                if={
                  income.additional_data &&
                  "supervisor" in income.additional_data &&
                  income.additional_data.supervisor?.email
                }
              >
                <KeyValue
                  label="Supervisor email"
                  value={
                    income.additional_data &&
                    "supervisor" in income.additional_data
                      ? income.additional_data.supervisor?.email
                      : ""
                  }
                />
              </Show>
              <Show
                if={
                  income.additional_data &&
                  "supervisor" in income.additional_data &&
                  income.additional_data.supervisor?.phone
                }
              >
                <KeyValue
                  label="Supervisor phone"
                  value={
                    income.additional_data &&
                    "supervisor" in income.additional_data &&
                    income.additional_data.supervisor?.phone
                      ? formatPhone(income.additional_data.supervisor.phone)
                      : undefined
                  }
                />
              </Show>

              <KeyValue
                label="Annual income"
                value={toMoney(annualIncomeCents / 100)}
              />
            </ContentTitle>

            <ContentTitle title="Verification method">
              <div className="mb-4 space-y-3">
                {income.type === "offer_letter"
                  ? income.assets.map((a, aIdx) => {
                      return (
                        <AssetItem
                          key={`${income.type}_${a.asset?.url}____${aIdx}`}
                          url={a.asset?.url || ""}
                          content_type={a.asset?.content_type || ""}
                          filename={a.asset?.filename || "Document"}
                        />
                      );
                    })
                  : null}
              </div>

              <div className="flex justify-between mb-4">
                <div className="flex gap-4 items-center">
                  <Logo
                    logo={
                      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                      income.items[0] &&
                      "institution_metadata" in income.items[0].data
                        ? income.items[0].data.institution_metadata?.logo || ""
                        : ""
                    }
                    verified_source_type={income.verified_source_type}
                  />
                  <div>
                    <h2 className="text-base text-content-1000 font-medium mb-1">
                      {getVerificationTitleName(income)}
                    </h2>
                    <p className="text-sm text-content-400">
                      {verificationSourceType[income.verified_source_type] ||
                        income.verified_source_type}
                    </p>
                  </div>
                </div>
                {/* 

                  // For future self actually add this check for OnHold when we have this flow

                <Button size={ButtonSize.SM} disabled={applicationStatus === ApplicationStatus.OnHold}
                 tooltip={
                 Add tooltip for onhold here 
                tooltipDelay={250}
                  applicationId={applicationId} applicationStatus={applicationStatus} />}
                >
                  Re-verify with documents
                </Button> 
                */}
              </div>

              <div className="space-y-3">
                {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                {income.items[0] &&
                "accounts" in income.items[0].data &&
                income.items[0].data.accounts[0] ? (
                  <div className="space-y-3">
                    <KeyValue
                      label="Account ID"
                      value={income.items[0].data.accounts[0].account_id}
                    />
                  </div>
                ) : null}
                {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  income.items[0] && "account" in income.items[0].data ? (
                    <>
                      <KeyValue
                        label="Bank account"
                        value={
                          capitalize(income.items[0].data.account.type) +
                          " ***" +
                          income.items[0].data.account.mask
                        }
                      />
                      <KeyValue
                        label="Institution"
                        value={income.items[0].data.account.name || "Unknown"}
                      />

                      <KeyValue
                        label="Source bank account"
                        value={
                          capitalize(income.items[0].data.account.type) +
                          " ***" +
                          income.items[0].data.account.mask
                        }
                      />
                    </>
                  ) : null
                }
                {/* half of the data is missing from BE */}
              </div>

              {income.items.length ? (
                <TablesSlice income={income} />
              ) : (
                <p className="text-base text-content-500">
                  No verified income sources
                </p>
              )}
            </ContentTitle>
          </Section>
        );
      })}
      <Section sectionTitle="Projected debt to income ratio" maxWidth="none">
        <div className="flex md:flex-row flex-col gap-y-2 w-full items-center py-8 gap-6">
          <div className="flex md:flex-col text-sm gap-2">
            <p className="text-content-1000 font-medium">Monthly debt</p>
            <p className="text-content-300 font-medium md:text-left text-center">
              {toMoney(summary.total_monthly_debt.cents / 100)}
            </p>
          </div>

          <RoundedMathSymbol symbol="/" />
          <div className="flex md:flex-col gap-2 text-sm">
            <p className="text-content-1000 font-medium">Net monthly income</p>
            <p className="text-content-300 md:text-left text-center">
              {toMoney(summary.verified_net_monthly_total)}
            </p>
          </div>

          <RoundedMathSymbol symbol="X" />

          <p className="text-content-1000 font-medium md:text-left text-center text-sm">
            100
          </p>

          <RoundedMathSymbol symbol="=" />

          <div className="flex md:flex-col gap-2 text-sm">
            <p className="text-content-1000 font-medium">
              Debt to income ratio
            </p>
            <p className="text-content-300 font-medium  md:text-left text-center">
              {summary.debt_income_ratio || 0}%
            </p>
          </div>
        </div>
        <Show if={debts.length > 0}>
          <ContentTitle
            title="Monthly debt includes"
            contentClassName="flex gap-4 flex-col"
          >
            {debts.map((debt) => {
              return (
                <div
                  className="flex justify-between items-center"
                  key={debt.name + debt.amount}
                >
                  <div className="flex gap-3 items-center">
                    {debt.name === "listed_rent_amount" ? (
                      <HugeiconsIcon
                        icon={Home05Icon}
                        size={20}
                        strokeWidth={1}
                        className="text-content-400"
                      />
                    ) : (
                      <HugeiconsIcon
                        icon={CreditCardIcon}
                        size={20}
                        strokeWidth={1}
                        className="text-content-400"
                      />
                    )}

                    <div className="space-y-1 text-content-400">
                      <p className=" text-sm font-medium first-letter:uppercase">
                        {debt.name.replaceAll("_", " ") || "-"}
                      </p>
                      <p className="text-xs">{debt.description || "-"}</p>
                    </div>
                  </div>

                  <p className="text-content-1000 font-medium text-sm">
                    {toMoney(debt.amount.cents / 100)}
                  </p>
                </div>
              );
            })}
          </ContentTitle>
        </Show>
      </Section>

      <Show if={Boolean(showEditIncomeModal)}>
        <EditIncomeModal
          onSuccess={refetch}
          applicationId={Number(applicationId || 0)}
          applicant={applicant}
          verificationId={verificationId || 0}
          opened={Boolean(showEditIncomeModal)}
          onClose={() => setShowEditIncomeModal(false)}
          {...(typeof showEditIncomeModal === "object"
            ? showEditIncomeModal
            : {})}
        />
      </Show>
    </div>
  );
};
